import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import CloseRoundedIcon       from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import FingerprintIcon        from '@mui/icons-material/Fingerprint';
import { getComparisonCode } from '../../services/winnowingApi';
import {
  SURFACE_LIGHT,
  TOOLBAR_BG_DARK,
  TOOLBAR_BG_LIGHT,
  INDIGO,
  DANGER,
  WARNING,
  SUCCESS,
} from '../../utils/colors';

// Amber used for fingerprint-match highlights
const MATCH_COLOR        = '#f59e0b';
const MATCH_BG_DARK      = 'rgba(245,158,11,0.13)';
const MATCH_BG_LIGHT     = 'rgba(245,158,11,0.10)';
const MATCH_BORDER       = `3px solid ${MATCH_COLOR}`;
const MATCH_BORDER_INVIS = '3px solid transparent';

// ── Similarity badge ──────────────────────────────────────────────────────────

function SimilarityBadge({ value }) {
  const color  = value > 70 ? DANGER  : value > 40 ? WARNING : SUCCESS;
  const label  = `${value}% similar`;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: `${color}18`,
        color,
        fontWeight: 700,
        border: `1px solid ${color}50`,
        fontSize: '0.72rem',
      }}
    />
  );
}

// ── Code pane ─────────────────────────────────────────────────────────────────

/**
 * CodePane
 *
 * Renders one student's submission code inside the CodeComparisonModal.
 * Lines whose 1-based number appears in `matchedLines` are highlighted with
 * an amber left-border and background to indicate Winnowing fingerprint matches
 * (i.e. copied / plagiarised blocks detected by the backend pipeline).
 *
 * Props:
 *   title        {string}    – display label for the pane header
 *   data         {object}    – { code, language, submittedAt }
 *   side         {'A'|'B'}   – determines the pane accent colour
 *   matchedLines {number[]}  – 1-based line numbers to highlight (default: [])
 */
function CodePane({ title, data, side, matchedLines = [] }) {
  const [copied, setCopied] = useState(false);

  // Convert to a Set for O(1) per-line lookups
  const matchedSet = useMemo(() => new Set(matchedLines), [matchedLines]);

  const handleCopy = () => {
    if (data?.code) {
      navigator.clipboard.writeText(data.code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  const borderColor = side === 'A' ? INDIGO : DANGER;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        border: `1.5px solid ${borderColor}40`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* ── Pane header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: `${borderColor}14`,
          borderBottom: `1px solid ${borderColor}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: borderColor }} />
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          {data?.language && (
            <Chip label={data.language} size="small" variant="outlined"
              sx={{ fontSize: '0.68rem', height: 18 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {data?.submittedAt && (
            <Typography variant="caption" color="text.secondary">
              {new Date(data.submittedAt).toLocaleString()}
            </Typography>
          )}
          {data?.code && (
            <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyRoundedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Code body ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 0,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? '#0d1117' : SURFACE_LIGHT,
        }}
      >
        {data?.code ? (
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.78rem',
              fontFamily: '"Fira Code", "JetBrains Mono", "Courier New", monospace',
              lineHeight: 1.7,
            }}
          >
            <Box component="tbody">
              {data.code.split('\n').map((line, i) => {
                const lineNo    = i + 1;
                const isMatched = matchedSet.has(lineNo);

                return (
                  <Box
                    key={i}
                    component="tr"
                    sx={{
                      bgcolor: isMatched
                        ? (theme) => theme.palette.mode === 'dark' ? MATCH_BG_DARK : MATCH_BG_LIGHT
                        : 'transparent',
                      borderLeft: isMatched ? MATCH_BORDER : MATCH_BORDER_INVIS,
                      // Subtle transition so the highlight doesn't feel jarring
                      transition: 'background-color 0.15s',
                    }}
                  >
                    {/* ── Gutter: line number ── */}
                    <Box
                      component="td"
                      sx={{
                        width: 42,
                        minWidth: 42,
                        textAlign: 'right',
                        px: 1,
                        userSelect: 'none',
                        color: isMatched ? MATCH_COLOR : 'rgba(128,128,128,0.5)',
                        fontWeight: isMatched ? 700 : 400,
                        borderRight: (theme) =>
                          `1px solid ${theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(0,0,0,0.08)'}`,
                        fontSize: '0.70rem',
                      }}
                    >
                      {lineNo}
                    </Box>

                    {/* ── Match indicator column ── */}
                    <Box
                      component="td"
                      sx={{
                        width: 16,
                        minWidth: 16,
                        textAlign: 'center',
                        userSelect: 'none',
                        fontSize: '0.60rem',
                        color: MATCH_COLOR,
                        fontWeight: 700,
                        pl: 0.25,
                        pr: 0,
                      }}
                    >
                      {isMatched ? '◀' : ''}
                    </Box>

                    {/* ── Code content ── */}
                    <Box
                      component="td"
                      sx={{
                        pl: 1.5,
                        pr: 2,
                        whiteSpace: 'pre',
                        overflowX: 'visible',
                        width: '100%',
                        color: (theme) =>
                          theme.palette.mode === 'dark' ? '#e6edf3' : '#1f2933',
                      }}
                    >
                      {/* Preserve empty lines — <pre> would collapse them */}
                      {line || '\u200b'}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No code available for this submission.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

/**
 * CodeComparisonModal
 *
 * Full-screen dialog showing side-by-side source code for two students
 * on the same LeetCode problem.
 *
 * Props:
 *   open          {boolean}
 *   onClose       {function}
 *   row           {object}  – a plagiarism result row: { studentA, studentB, problemSlug, winnowingSimilarity }
 */
export default function CodeComparisonModal({ open, onClose, row }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [codes,   setCodes]   = useState(null);   // { studentA: {...}, studentB: {...} }

  useEffect(() => {
    if (!open || !row) {
      setCodes(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    getComparisonCode(row.studentA, row.studentB, row.problemSlug)
      .then(({ data }) => setCodes(data))
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load code.'))
      .finally(() => setLoading(false));
  }, [open, row]);

  if (!row) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? TOOLBAR_BG_DARK : TOOLBAR_BG_LIGHT,
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700} fontSize="1rem">
            Code Comparison
          </Typography>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Chip
            label={row.problemSlug}
            size="small"
            sx={{
              bgcolor: `${INDIGO}18`,
              color: INDIGO,
              fontWeight: 700,
              fontSize: '0.75rem',
            }}
          />
          {row.winnowingSimilarity != null && (
            <SimilarityBadge value={row.winnowingSimilarity} />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          gap: 1.5,
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* ── Fingerprint match legend ─────────────────────────────── */}
            {(codes?.matchedLines?.A?.length > 0 || codes?.matchedLines?.B?.length > 0) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: `${MATCH_COLOR}0f`,
                  border: `1px solid ${MATCH_COLOR}30`,
                  flexShrink: 0,
                }}
              >
                <FingerprintIcon sx={{ fontSize: 16, color: MATCH_COLOR }} />
                <Typography variant="caption" sx={{ color: MATCH_COLOR, fontWeight: 700 }}>
                  Fingerprint matches highlighted
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  — amber lines contain k-gram hashes present in both submissions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                  <Chip
                    label={`${codes.matchedLines.A.length} line${codes.matchedLines.A.length !== 1 ? 's' : ''} in ${row.studentA}`}
                    size="small"
                    sx={{ bgcolor: `${INDIGO}18`, color: INDIGO, fontSize: '0.68rem', height: 20, fontWeight: 700 }}
                  />
                  <Chip
                    label={`${codes.matchedLines.B.length} line${codes.matchedLines.B.length !== 1 ? 's' : ''} in ${row.studentB}`}
                    size="small"
                    sx={{ bgcolor: `${DANGER}18`, color: DANGER, fontSize: '0.68rem', height: 20, fontWeight: 700 }}
                  />
                </Box>
              </Box>
            )}

            {/* ── Side-by-side code panes ──────────────────────────────── */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              <CodePane
                title={`${row.studentA}`}
                data={codes?.studentA}
                side="A"
                matchedLines={codes?.matchedLines?.A ?? []}
              />
              <CodePane
                title={`${row.studentB}`}
                data={codes?.studentB}
                side="B"
                matchedLines={codes?.matchedLines?.B ?? []}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
